import { useMemo } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRightLeft, Undo2, Users } from "lucide-react";

const ROLE_LABEL: Record<string, string> = {
  chairman: "Chairman",
  secretary: "Secretary",
  member: "Member",
};

const DECISION_STYLE: Record<string, string> = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const DECISION_LABEL: Record<string, string> = {
  approved: "recommends",
  rejected: "does not recommend",
};

const day = (s?: string | null) => (s ? String(s).slice(0, 10) : null);

/** One row of the lens: the application, and this member's assignment on it. */
type Held = { app: any; review: any };

/**
 * Everything one panel member is carrying in this cycle.
 *
 * Split into what they have ASSESSED and what is still WAITING on them,
 * because those are two different conversations: the first is a record of work
 * done, the second is the chase list. A single mixed table makes the admin do
 * that sorting by eye every time they open it.
 *
 * Reads the rows the cycle screen already holds rather than fetching again —
 * the reviewer chips in the table and this dialog are then incapable of
 * disagreeing about who holds what. It also means a grade filter narrows this
 * the same way it narrows the table, which is what an admin working one grade
 * expects; the header says so.
 */
export default function CommitteePanelMemberModal({
  member,
  rows,
  gradeNote,
  canAct,
  busy,
  onMove,
  onWithdraw,
  onClose,
}: {
  member: any;
  /** The cycle's applications, each with its `reviews`. */
  rows: any[];
  /** "Fellow only", when the screen is scoped to one grade. */
  gradeNote?: string | null;
  canAct: boolean;
  busy: boolean;
  onMove: (held: Held) => void;
  onWithdraw: (held: Held) => void;
  onClose: () => void;
}) {
  /* Every application in the cycle this member has an assignment on, paired
     with that assignment. Flattened off the applications rather than looked up
     per member, because an application carries several reviewers and it is the
     one row belonging to THIS member that the actions act on. */
  const held: Held[] = useMemo(() => {
    const mine: Held[] = [];

    for (const app of rows) {
      for (const review of app.reviews ?? []) {
        if (String(review.member_id ?? "") === String(member.member_id)) {
          mine.push({ app, review });
        }
      }
    }

    return mine.sort((a, b) =>
      String(a.app.name ?? "").localeCompare(String(b.app.name ?? "")),
    );
  }, [rows, member.member_id]);

  const awaiting = held.filter((h) => String(h.review.decision ?? "pending") === "pending");
  const assessed = held.filter((h) => String(h.review.decision ?? "pending") !== "pending");

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 md:p-8">
      <div className="w-full max-w-3xl bg-white rounded-[28px] shadow-2xl border border-gray-100 overflow-hidden">
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-indigo-50 border border-indigo-100 rounded-2xl flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-black text-gray-900 tracking-tight truncate">
                {member.name}
              </h2>
              <p className="text-xs font-semibold text-gray-500">
                {[ROLE_LABEL[member.committee_role] ?? member.committee_role, member.membership_no]
                  .filter(Boolean)
                  .join(" · ")}
                {" · "}
                {held.length} in this cycle
                {gradeNote ? ` · ${gradeNote}` : ""}
              </p>
              {member.is_former ? (
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-0.5">
                  No longer on the committee — their recommendations stand
                </p>
              ) : null}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-50 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[65vh] overflow-y-auto">
          {held.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nothing has been handed to {member.name} in this cycle
              {gradeNote ? ` for ${gradeNote.toLowerCase()}` : ""} yet.
            </p>
          ) : (
            <>
              {/* The chase list is where moving work belongs — this is the
                  screen an admin is on when they decide somebody has had long
                  enough. The assessed table takes no actions: a recorded
                  recommendation is theirs and part of the record. */}
              <HeldRows
                title={`Awaiting their recommendation (${awaiting.length})`}
                rows={awaiting}
                empty="They have reported on everything handed to them."
                actionable={canAct}
                busy={busy}
                onMove={onMove}
                onWithdraw={onWithdraw}
              />
              <HeldRows
                title={`Assessed (${assessed.length})`}
                rows={assessed}
                empty="They have not reported on any of these yet."
              />
            </>
          )}
        </div>

        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50/60">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function HeldRows({
  title,
  rows,
  empty,
  actionable,
  busy,
  onMove,
  onWithdraw,
}: {
  title: string;
  rows: Held[];
  empty: string;
  /** Omitted on the assessed table, which takes no actions. */
  actionable?: boolean;
  busy?: boolean;
  onMove?: (held: Held) => void;
  onWithdraw?: (held: Held) => void;
}) {
  const acts = !!actionable && !!onMove && !!onWithdraw;

  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2">
        {title}
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">{empty}</p>
      ) : (
        <div className="border rounded-2xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                <th className="px-3 py-2">Applicant</th>
                <th className="px-3 py-2">Upgrade</th>
                <th className="px-3 py-2">Their verdict</th>
                <th className="px-3 py-2 text-right">Waiting</th>
                {acts ? <th className="px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map(({ app, review }) => {
                const decision = String(review.decision ?? "pending");

                return (
                  <tr key={review.id} className="hover:bg-gray-50/60">
                    <td className="px-3 py-2">
                      <Link
                        to={`/admin/upgrade-applications/${app.id}`}
                        className="font-semibold text-gray-900 hover:text-indigo-600"
                      >
                        {app.name || "—"}
                      </Link>
                      <div className="text-[11px] text-gray-400">{app.membership_no || "—"}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {[app.from_grade, app.to_grade].filter(Boolean).join(" → ") || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {decision === "pending" ? (
                        <span className="text-[11px] font-semibold text-amber-600">pending</span>
                      ) : (
                        <>
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                              DECISION_STYLE[decision] ?? "bg-gray-50 text-gray-600 border-gray-200"
                            }`}
                          >
                            {DECISION_LABEL[decision] ?? decision}
                          </span>
                          {/* When, not just what — the date is how an admin
                              tells a recommendation given this sitting from one
                              carried in from an earlier one. */}
                          {day(review.decided_at) ? (
                            <div className="text-[11px] text-gray-400 mt-0.5">
                              {day(review.decided_at)}
                              {review.recorded_by_name ? ` · keyed by ${review.recorded_by_name}` : ""}
                            </div>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums font-bold ${
                        (app.waiting_days ?? 0) > 30 ? "text-rose-600" : "text-gray-500"
                      }`}
                    >
                      {app.waiting_days != null ? `${app.waiting_days}d` : "—"}
                    </td>
                    {acts ? (
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {/* Labelled, not icon-only: a bare X beside somebody's
                            name reads as "delete this person". */}
                        <button
                          type="button"
                          onClick={() => onMove!({ app, review })}
                          disabled={busy}
                          title="Give this application to a different committee member"
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-100 text-[11px] font-bold text-gray-500 hover:text-blue-600 hover:border-blue-200 disabled:opacity-50"
                        >
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                          Reassign
                        </button>
                        <button
                          type="button"
                          onClick={() => onWithdraw!({ app, review })}
                          disabled={busy}
                          title="Take it off this member and leave the application unassigned"
                          className="ml-1.5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-100 text-[11px] font-bold text-gray-500 hover:text-amber-600 hover:border-amber-200 disabled:opacity-50"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                          Withdraw
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
